import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { ApiError, api, unwrap } from "../../api/client";
import { keys } from "../../api/hooks";
import { AuthFrame } from "../../components/shell/AuthFrame";
import { type Key, t } from "../../i18n";
import { inAppPath } from "../../lib/links";

type Search = { token?: string; next?: string };

export const Route = createFileRoute("/auth/verify")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    token: typeof s.token === "string" ? s.token : undefined,
    // Where the person was going (R10-N8): one of the app's own pages only.
    next: typeof s.next === "string" && inAppPath(s.next) ? s.next : undefined,
  }),
  component: Verify,
});

// The words by key, read when the page renders.
const DEAD_LINK: Record<string, { title: Key; body: Key }> = {
  used: { title: "shell.linkUsedTitle", body: "shell.linkUsedBody" },
  expired: { title: "shell.linkExpiredTitle", body: "shell.linkExpiredBody" },
  invalid: { title: "shell.linkInvalidTitle", body: "shell.linkInvalidBody" },
};

/** Opening the link only shows this page; the Sign in button's POST signs in, so mail
 *  scanners that prefetch links can't use them up (PLAN §8.11). */
function Verify() {
  const { token, next } = Route.useSearch();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const state = useQuery({
    queryKey: ["link", token],
    enabled: Boolean(token),
    retry: false,
    queryFn: () =>
      unwrap(api.GET("/api/auth/verify", { params: { query: { token: token ?? "" } } })),
  });

  const status = token ? state.data?.state : "invalid";
  if (token && state.isPending) return <AuthFrame>{null}</AuthFrame>;

  if (status !== "valid") {
    const copy = DEAD_LINK[status ?? "invalid"] ?? DEAD_LINK.invalid;
    return (
      <AuthFrame>
        <div className="auth-card">
          <h2>{copy && t(copy.title)}</h2>
          <p>{copy && t(copy.body)}</p>
          <Link to="/signin" search={next ? { next } : {}} className="btn btn-primary btn-block">
            {t("shell.askNewLink")}
          </Link>
        </div>
      </AuthFrame>
    );
  }

  const signIn = async () => {
    setBusy(true);
    setError(null);
    try {
      const me = await unwrap(api.POST("/api/auth/verify", { body: { token: token ?? "" } }));
      qc.setQueryData(keys.me, me);
      navigate({ href: next ?? "/" });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("shell.couldntReach"));
      setBusy(false);
    }
  };

  return (
    <AuthFrame>
      <div className="auth-card">
        <h2>{t("shell.signInToVantage")}</h2>
        <p>{t("shell.linkReady")}</p>
        {error && <div className="form-error">{error}</div>}
        <button
          type="button"
          className="btn btn-primary btn-block"
          disabled={busy}
          onClick={signIn}
        >
          {t("shell.signIn")}
        </button>
      </div>
    </AuthFrame>
  );
}
